<template>
  <div
    class="viluu-landing"
    :class="{
      'is-leaving': isLeaving,
      'start-animations': animationsStarted,
      'from-bfcache': fromBfcache,
    }"
  >
    <bg-blobs />

    <the-nav />
    <hero-section />
    <bottom-features />
    <how-it-works-section />
    <quiz-match-section />
    <stories-section />
    <top-choice-section />
    <trust-section />
    <discover-section />
    <faq-section />
    <big-cta-section />
    <the-footer />

    <signup-modal />
    <restore-account-modal />
    <cookie-banner />
  </div>
</template>

<script>
import { landingChromeMixin } from './landingChrome';
import BgBlobs from '../../../components/LandingV2/BgBlobs.vue';
import CookieBanner from '../../../components/LandingV2/CookieBanner.vue';
import TheNav from '../../../components/LandingV2/sections/TheNav.vue';
import HeroSection from '../../../components/LandingV2/sections/HeroSection.vue';
import BottomFeatures from '../../../components/LandingV2/sections/BottomFeatures.vue';
import HowItWorksSection from '../../../components/LandingV2/sections/HowItWorksSection.vue';
import QuizMatchSection from '../../../components/LandingV2/sections/QuizMatchSection.vue';
import StoriesSection from '../../../components/LandingV2/sections/StoriesSection.vue';
import TopChoiceSection from '../../../components/LandingV2/sections/TopChoiceSection.vue';
import TrustSection from '../../../components/LandingV2/sections/TrustSection.vue';
import DiscoverSection from '../../../components/LandingV2/sections/DiscoverSection.vue';
import FaqSection from '../../../components/LandingV2/sections/FaqSection.vue';
import BigCtaSection from '../../../components/LandingV2/sections/BigCtaSection.vue';
import TheFooter from '../../../components/LandingV2/sections/TheFooter.vue';
import SignupModal from '../../../components/LandingV2/signup/SignupModal.vue';
import RestoreAccountModal from '../../../components/LandingV2/signup/RestoreAccountModal.vue';
import { userAwaitingEmailVerification } from '../../../components/LandingV2/signup/helpers';
import { initLandingAnalytics } from '../../../analytics/landing';
import { trackEvent } from '../../../analytics/ga4';

// ResetPasswordController@showResetForm renders the landing with this card flag
// (mirrors the legacy home-classic auth-card). It's our cue to open the modal
// straight onto the set-new-password step.
const RESET_PASSWORD_CARD = 'auth-reset-password-email-card';
const RESTORE_ACCOUNT_CARD = 'auth-restore-account-card';
const LOGIN_CARD = 'auth-login-card';
const REGISTER_CARD = 'auth-register-card';

export default {
  name: 'ViluuLanding',
  mixins: [landingChromeMixin],
  components: {
    BgBlobs,
    CookieBanner,
    TheNav,
    HeroSection,
    BottomFeatures,
    HowItWorksSection,
    QuizMatchSection,
    StoriesSection,
    TopChoiceSection,
    TrustSection,
    DiscoverSection,
    FaqSection,
    BigCtaSection,
    TheFooter,
    SignupModal,
    RestoreAccountModal,
  },
  props: {
    // Forwarded from the Blade view; set by ResetPasswordController to flag a
    // password-reset link landing here. Empty for the normal marketing landing.
    cardComponent: { type: String, default: '' },
  },
  data() {
    return {
      animationsStarted: false,
    };
  },
  mounted() {
    // Trigger hero entry animations once the page is interactive.
    window.addEventListener('load', this.startAnimations);
    if (document.readyState === 'complete') {
      this.startAnimations();
    }

    this.$nextTick(() => {
      initLandingAnalytics();
    });
    this.maybeFireSocialAuthEvents();

    if (this.cardComponent === RESET_PASSWORD_CARD) {
      // Reset links arrive anonymous, so resume-registration never applies here.
      this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'reset' });
    } else if (this.cardComponent === RESTORE_ACCOUNT_CARD) {
      this.$eventHub.$emit('viluu-landing:open-restore-account-modal', {});
    } else if (this.cardComponent === LOGIN_CARD) {
      this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'login' });
    } else if (this.cardComponent === REGISTER_CARD) {
      this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'register' });
    } else {
      this.checkResumeRegistration();
    }
  },
  beforeDestroy() {
    window.removeEventListener('load', this.startAnimations);
  },
  methods: {
    startAnimations() {
      this.animationsStarted = true;
    },
    maybeFireSocialAuthEvents() {
      // Flash from OAuth callback when a new social account was created (SOI).
      const signUpMeta = document.querySelector('meta[name="viluu-ga-sign-up"]');
      if (signUpMeta) {
        const method = signUpMeta.getAttribute('content') || 'unknown';
        const cta = signUpMeta.getAttribute('data-cta-location') || 'unknown';
        trackEvent('sign_up', { method, cta_location: cta });
        signUpMeta.remove();
      }
      const loginMeta = document.querySelector('meta[name="viluu-ga-login"]');
      if (loginMeta) {
        trackEvent('login', {
          method: loginMeta.getAttribute('content') || 'unknown',
          remember_me: false,
        });
        loginMeta.remove();
      }
    },
    // Mirror legacy Card.vue.initialComponent(): if there's an authenticated
    // user mid-registration or awaiting email verification, pull their state
    // from ajax.auth.users.show and reopen the signup modal. Without this,
    // the user sees the marketing landing, tries to log in again, and hits a
    // 403 from the `filled` middleware on /members.
    async checkResumeRegistration() {
      let resp;
      try {
        resp = await axios.get(zRoute('ajax.auth.users.show'));
      } catch (e) {
        return;
      }
      if (!resp || resp.status === 204) {
        return;
      }
      const user = resp.data && resp.data.data;
      if (!user) {
        return;
      }

      if (userAwaitingEmailVerification(user) || user.auth_step === 'email') {
        this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'register', continueUser: user });
        return;
      }

      const stepsHandledInModal = ['birthday', 'location', 'gender', 'preferences', 'name'];
      if (stepsHandledInModal.includes(user.auth_step)) {
        this.$eventHub.$emit('viluu-landing:open-signup', { mode: 'register', continueUser: user });
        return;
      }
      // Phone verification still lives in the classic auth-card.
      if (user.auth_step === 'phone') {
        window.location.href = zRoute('home.classic');
      }
    },
  },
};
</script>
